import React, { ReactNode, useEffect, useState } from "react";

import { getPackageConfig } from "components/ContentfulRenderer/ContentfulPackages/getPackageConfig";
import MobileFullPackages from "components/ContentfulRenderer/ContentfulPackages/MobileFullPackages/MobileFullPackages";
import InsurancePackagesTable from "components/ContentfulRenderer/ContentfulPackages/InsurancePackagesTable/InsurancePackagesTable";
import ShowOnMedia from "ui-kit/ShowOnMedia";
import Benefits from "components/ContentfulRenderer/ContentfulComponent/Benefits/Benefits";
import InnerLayout from "components/InnerLayout/InnerLayout";
import { getCopays, getDeductibles } from "utils/site";
import { getAmountToPay } from "utils/insurance/getAmountToPay";
import { amountToPay as calculateAmountToPayWithoutDiscounts } from "utils/insurance";
import styles from "./QuotePreview.module.css";
import { useWebTranslation } from "translations";
import { ProductEnum } from "../../../../../../__generated__/globalTypes";
import Competitor from "components/Signup/components/Competitor/Competitor";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import {
  useError,
  useIsLoading,
  useMetadata,
} from "components/Signup/store/selectors/metadata";
import {
  getCurrentDraftForAnimal,
  getDraft,
  useAllValidAnimals,
  useBillingInterval,
  useCurrentAnimal,
} from "components/Signup/store/selectors/animals";
import {
  updateBillingInterval,
  updateInsuranceById,
} from "components/Signup/store/slices/animals";
import InsurancePackages from "components/Signup/components/InsurancePackages";
import { Container } from "ui-kit/Layout";
import Gutter from "ui-kit/Gutter";
import { Button } from "ui-kit/Button";
import ChoosePaymentInterval from "components/Signup/components/ChoosePaymentInterval";
import { Typography } from "ui-kit/Typography";
import { DeductibleCalculatorBanner } from "components/Signup/components/DeductibleCalculator";
import DiscountCode from "components/Signup/components/DiscountCode";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import SignupDocuments from "components/Signup/components/SignupDocuments/SignupDocuments";
import {
  getCompetitorCopay,
  getCompetitorDeductible,
  getCompetitorName,
  getCompetitorRenewalDate,
} from "components/Signup/components/utils/insurely/insurelyUtil";
import ChooseStartDate from "components/Signup/components/ChooseStartDate";
import ChooseDeductible from "components/Signup/components/ChooseDeductible";
import { formatErrorMessage } from "utils/validation";
import {
  ipids,
  prepurchaseInfoInsurance,
  prepurchaseInfoLassie,
  termsAndConditions,
} from "components-shared/insuranceData";
import { StepProps } from "../../types";
import { updateCustomer } from "components/Signup/store/slices/customer";
import { useCustomer } from "components/Signup/store/selectors/customer";
import StickyBottom from "components/Signup/components/StickyBottom/StickyBottom";
import { BuyButton } from "components/Signup/components/BuyButton";
import { formatCurrency } from "utils/utils";
import { AnimalsTabs } from "components/Signup/components/AnimalsTabs";
import { QuotePreviewBottomBar } from "components/Signup/components/QuotePreviewBottomBar";
import { QPBanner } from "components/Signup/components/tractive/QPBanner";
import { Modal } from "ui-kit";

const QuotePreview = ({ next, transitioning, isValid }: StepProps) => {
  const { t } = useWebTranslation();

  const dispatch = useSignupDispatch();
  const animal = useCurrentAnimal();
  const insurance = useCurrentInsuranceByAnimalId(animal.id);
  const metadata = useMetadata();
  const customer = useCustomer();
  const animals = useAllValidAnimals();
  const billingInterval = useBillingInterval();

  const isLoading = useIsLoading();
  const error = useError();

  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    if (insurance.insurely?.shouldCompare) {
      const renewalDate = getCompetitorRenewalDate(insurance.insurely);

      if (renewalDate) {
        // onChangeStartDate({ date: renewalDate });
        dispatch(
          updateInsuranceById({
            id: insurance.id,
            update: { startDate: renewalDate },
          })
        );
      }
    }
  }, []);

  if (!insurance) {
    return null;
  }

  const currentDraft = getCurrentDraftForAnimal(animal);

  const packageConfig = getPackageConfig({
    product: animal.species as unknown as ProductEnum,
  });

  if (!packageConfig) {
    return null;
  }

  packageConfig.benefits = packageConfig.benefits.filter(
    (benefit) =>
      !["deductible", "lifeInsurance", "breederInsurance"].includes(
        benefit.name
      )
  );

  const packagePrices = packageConfig.packages.map((pack) =>
    getAmountToPay({
      ...currentDraft,
      billingInterval: insurance.billingInterval,
    })
  );

  const selectedIndex = packageConfig.packages.findIndex(
    (pack) =>
      pack.limitAddons?.includes(insurance.limit) ||
      insurance.limit === pack.limit
  );

  const competitorName = getCompetitorName(insurance.insurely);
  const competitorDeductible = getCompetitorDeductible(insurance.insurely);
  const competitorCopay = getCompetitorCopay(insurance.insurely);

  const amountToPay = getAmountToPay({
    ...currentDraft,
    billingInterval: insurance.billingInterval,
  });

  return (
    <div className={styles.fadeIn}>
      {animals.length > 1 && <AnimalsTabs />}
      {insurance.insurely?.policies ? (
        <>
          <Gutter size={2} />
          <Competitor state={insurance.insurely} />
        </>
      ) : null}
      <InsurancePackages
        packageConfig={packageConfig}
        packagePrices={packagePrices}
        selectedIndex={selectedIndex}
        onChange={({ selectedLimitAddon, limit }) => {
          dispatch(
            updateInsuranceById({
              id: insurance.id,
              update: {
                limit,
                ...(selectedLimitAddon && { selectedLimitAddon }),
              },
            })
          );
        }}
        onCompare={() => setShowComparison(true)}
        isCopyExperiment={true}
        insurance={insurance}
      />

      <Container>
        <Gutter size={4} />
        <Button
          variant="custom"
          textColor="blue300"
          icon="chevron-right"
          title={t("quote_preview.compare_packages")}
          onPress={() => setShowComparison(true)}
        />
        <Gutter size={4} />
        <ChooseStartDate />
        <Gutter size={6} />
        <ChooseDeductible
          currentTotal={calculateAmountToPayWithoutDiscounts({
            ...currentDraft,
            billingInterval: insurance.billingInterval,
          })}
          currentDeductible={insurance.deductible}
          currentCopay={insurance.copay}
          deductibles={getDeductibles().map((deductible) => ({
            value: deductible,
            total: calculateAmountToPayWithoutDiscounts({
              ...getDraft({
                drafts: insurance.drafts,
                limit: insurance.limit,
                deductible,
                copay: insurance.copay,
                product: insurance.product,
                preexistingConditions: insurance.preexistingConditions,
              }),
              billingInterval: insurance.billingInterval,
            }),
          }))}
          copays={getCopays().map((copay) => ({
            value: copay,
            total: calculateAmountToPayWithoutDiscounts({
              ...getDraft({
                drafts: insurance.drafts,
                limit: insurance.limit,
                deductible: insurance.deductible,
                copay,
                product: insurance.product,
                preexistingConditions: insurance.preexistingConditions,
              }),
              billingInterval: insurance.billingInterval,
            }),
          }))}
          onChangeDeductibleCopay={(deductible, copay) => {
            dispatch(
              updateInsuranceById({
                id: insurance.id,
                update: { deductible, copay },
              })
            );
          }}
          deductibleDescription={
            competitorDeductible
              ? t("quote_preview.competitor_deductible", {
                  competitorName: competitorName ?? "",
                  competitorDeductible,
                })
              : ""
          }
          copayDescription={
            competitorCopay
              ? t("quote_preview.competitor_copay", {
                  competitorName: competitorName ?? "",
                  competitorCopay,
                })
              : ""
          }
        />
        <Gutter size={6} />
        <DeductibleCalculatorBanner />

        {animals.length === 1 ? (
          <>
            <Gutter size={6} />
            <ChoosePaymentInterval
              value={billingInterval}
              onChange={(billingInterval) =>
                dispatch(updateBillingInterval(billingInterval))
              }
            />
          </>
        ) : null}

        <Gutter size={6} />
        <QPBanner species={animal.species} />
        <Gutter size={6} />

        <DiscountCode
          campaignCode={metadata.campaignCode}
          amountToPay={amountToPay}
          eurobonus={{
            memberId: customer.eurobonusMemberId ?? null,
            isValid: !!customer.eurobonusMemberIdValid,
            onSubmit: (memberId: string | null, isValid: boolean) => {
              dispatch(
                updateCustomer({
                  eurobonusMemberId: memberId,
                  eurobonusMemberIdValid: isValid,
                })
              );
            },
          }}
          lunar={{
            memberId: customer.lunarMemberId ?? null,
            isValid: !!customer.lunarMemberIdValid,
            onSubmit: (memberId: string | null, isValid: boolean) => {
              dispatch(
                updateCustomer({
                  lunarMemberId: memberId,
                  lunarMemberIdValid: isValid,
                })
              );
            },
          }}
          discount={{
            appliedCode: insurance.discountCode ?? "",
            onSubmit: (discountCode: string) =>
              dispatch(
                updateInsuranceById({
                  id: insurance.id,
                  update: { discountCode },
                })
              ),
            inProgress: isLoading,
          }}
          fees={currentDraft?.fees}
          billingInterval={insurance.billingInterval}
        />
        {error && (
          <>
            <Gutter size={10} />
            <ErrorMessage>
              <Typography color="error">{formatErrorMessage(error)}</Typography>
            </ErrorMessage>
          </>
        )}
      </Container>

      <InnerLayout backgroundColor="white" hideHeader>
        <Gutter size={10} />
        <Typography variant="h3" style={{ textAlign: "center" }}>
          {t("benefits.title")}
        </Typography>
        <Gutter size={4} />
        <Benefits />
        <Gutter size={8} />
      </InnerLayout>

      {animals.length > 1 ? (
        <QuotePreviewBottomBar next={next} />
      ) : (
        <>
          <StickyBottom
            button={
              <BuyButton
                oldAmount={
                  amountToPay.old ? formatCurrency(amountToPay.old) : null
                }
                newAmount={formatCurrency(amountToPay.new)}
                onPress={next}
                loading={isLoading || transitioning}
                disabled={!isValid}
              />
            }
            transparent
          />
          <Gutter size={4} />
        </>
      )}

      <Gutter size={4} />
      <SignupDocuments
        documents={[
          {
            title: t("quote_preview_desktop.important.links.0.description"),
            url: termsAndConditions,
          },
          {
            title: t("quote_preview_desktop.important.links.1.description"),
            url: prepurchaseInfoInsurance,
          },
          {
            title: t("quote_preview_desktop.important.links.2.description"),
            url: prepurchaseInfoLassie,
          },
          {
            title: t("quote_preview_desktop.important.links.3.description"),
            url:
              ipids.find((ipid) => ipid.limit === insurance.limit)?.href ?? "",
          },
        ]}
      />
      <Modal
        isOpen={showComparison}
        title={t("quote_preview.compare_packages_overlay")}
        onClose={() => setShowComparison(false)}
        width="full"
      >
        <>
          <ShowOnMedia desktop={false} tablet={true} mobile={true}>
            <MobileFullPackages
              config={packageConfig}
              defaultSelectedIndex={selectedIndex}
              expanded
            />
          </ShowOnMedia>
          <ShowOnMedia desktop={true} tablet={false} mobile={false}>
            <InsurancePackagesTable config={packageConfig} />
          </ShowOnMedia>
        </>
      </Modal>
    </div>
  );
};

export default QuotePreview;
